import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MdComment, MdPhotoAlbum, MdSettings } from 'react-icons/md';
import { IoChatbox, IoNewspaper, IoSwapHorizontalSharp } from 'react-icons/io5';
import { Badge, Box, Button, IconButton, Popover, Switch, Tooltip } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import { ApiServerUrl } from '../../../../config.json';
import { TrioIDesignIcon } from './TrioIDesignIcon';
import { ISDViewerIcon } from './ISDViewerIcon';
import { useSelector } from 'react-redux';
import chatStyle from '../../../../components/chat/chat.module.css';
import { useLang } from '../../../../util/lang';
import { useWindowWidth } from '../../../../util/useWindowWidth';
import './ToolsMenuButton.css';
const TOOLBAR_FONT_FAMILY =
    '"Montserrat", "Microsoft JhengHei", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif';
const TOOLTIP_FONT_FAMILY =
    '"Montserrat-SemiBold", "Montserrat SemiBold", "Montserrat", "Microsoft JhengHei", sans-serif';
const TOOL_LABEL_FONT_FAMILY = '"Montserrat", "Microsoft JhengHei", sans-serif';

const I_DESIGN_REFERENCE_SCALE = 0.8;
const I_DESIGN_REFERENCE_INVERSE_WIDTH = `${100 / I_DESIGN_REFERENCE_SCALE}%`;
const QUICK_BUTTON_WIDTH_SHRINK = '46px';
const QUICK_BUTTON_HEIGHT_SHRINK = '46px';
const QUICK_BUTTON_BORDER_RADIUS_ROUND = '50%';
const QUICK_BUTTON_WIDTH = '67px';
const QUICK_BUTTON_HEIGHT = '75px';
const QUICK_BUTTON_BORDER_RADIUS = '9px';
const QUICK_BUTTON_PADDING = '9px 7px 2px 7px';
const TOOLBAR_VERTICAL_PADDING = 10;
const QUICK_BUTTON_LABEL_FONT_SIZE = '11px';
const QUICK_BUTTON_LABEL_LINE_HEIGHT = '12px';
const QUICK_BUTTON_LABEL_FONT_WEIGHT = 600;
const QUICK_BUTTON_LABEL_HEIGHT = '20px';
const QUICK_BUTTON_ICON_SIZE = 34;
const COMPACT_QUICK_BUTTON_ICON_SIZE = 28;
const ONLY_TRIO_SETTINGS_BUTTON_SIZE = 66;
const FLOATING_SETTINGS_DEFAULT_TOP = 150;
const FLOATING_SETTINGS_DEFAULT_RIGHT = 30;
const QUICK_BUTTON_HORIZONTAL_MARGIN = '2px';
const I_DESIGN_LETTER_SPACING = '0.5px';
const I_DESIGN_TEXT_RENDERING = {
    WebkitFontSmoothing: 'antialiased',
    MozOsxFontSmoothing: 'grayscale',
    textRendering: 'optimizeLegibility',
};
const I_DESIGN_TOOLTIP_RENDERING = {
    WebkitFontSmoothing: 'subpixel-antialiased',
    MozOsxFontSmoothing: 'auto',
    textRendering: 'optimizeLegibility',
};
const I_DESIGN_PRIMARY_PURPLE = '#532a74';
const I_DESIGN_MENU_BACKGROUND = '#eeeeef';
const I_DESIGN_MORE_MENU_BACKGROUND = '#544e8d';
const I_DESIGN_TOOLTIP_BACKGROUND = '#532a74';
const IFRAME_COMPACT_TOOLBAR_BREAKPOINT = 993;
const ToolsMenuSwitch = withStyles({
    root: {
        width: 34,
        height: 20,
        padding: 0,
        margin: 8,
    },
    switchBase: {
        padding: 1,
        '&$checked': {
            transform: 'translateX(14px)',
            color: '#fff',
            '& + $track': {
                backgroundColor: '#00a6e9',
                opacity: 1,
                border: 'none',
            },
        },
    },
    thumb: {
        width: 18,
        height: 18,
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.24)',
    },
    track: {
        borderRadius: 10,
        backgroundColor: '#e9e9ea',
        opacity: 1,
        transition: 'background-color 150ms ease',
    },
    checked: {},
})(Switch);
const IDesignTooltip = withStyles(() => ({
    tooltip: {
        width: 'auto',
        maxWidth: 250,
        backgroundColor: I_DESIGN_TOOLTIP_BACKGROUND,
        borderRadius: 6,
        boxShadow: '0 6px 16px rgba(35, 20, 52, 0.24)',
        padding: '8px 8px',
        margin: 0,
        fontFamily: TOOLTIP_FONT_FAMILY,
        fontSize: '14px',
        textTransform: 'none',
        letterSpacing: I_DESIGN_LETTER_SPACING,
        whiteSpace: 'nowrap',
        textAlign: 'center',
        // lineHeight: '1.4',
        ...I_DESIGN_TOOLTIP_RENDERING,
        fontWeight: '600 !important',
    },
    arrow: {
        color: I_DESIGN_TOOLTIP_BACKGROUND,
        fontSize: 16,
        '&::before': {
            marginTop: '3px', // Reset this to 0 to kill the gap line
            borderRadius: '2px', // This creates the rounded corner
            backgroundColor: I_DESIGN_TOOLTIP_BACKGROUND,
        },
    },
    tooltipPlacementBottom: {
        margin: '10.5px 0 !important',
    },

}))(Tooltip);

const IDesignMenuTooltip = withStyles(() => ({
    popper: {
        position: 'relative',
        top: '-5px !important',
    },
    tooltip: {
        width: 'auto',
        maxWidth: 'min(370px, 95svw)',
        backgroundColor: I_DESIGN_MORE_MENU_BACKGROUND,
        borderRadius: 8,
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.12)',
        padding: '6px',
        margin: 0,
        color: '#fff',
        fontFamily: TOOLBAR_FONT_FAMILY,
        ...I_DESIGN_TEXT_RENDERING,
    },
}))(Tooltip);

const ToolsMenuButton = ({
    showPhotos,
    showTrioIDesign,
    isShowSmileDesignFeature,
    comment,
    showChatbox,
    switchPositionOfiDesignAndSmileDesign,
    setShowPhotos,
    setShowTrioIDesign,
    setIsShowSmileDesignFeature,
    setShowChatbox,
    setSwitchPositionOfiDesignAndSmileDesign,
    setCommentBox,
    isloaded,
    isPatientSmileDesignHave,
    currentTreatmentPlan,
    canApproveCurrentISDPlan,
    handleTopApproveTreatmentPlan,
    gridTemplateArray,
    setIsCompactToolbar,
    onlyTrioIDesignVisible = false,
    isTrioIDesignRightmost = false,
}) => {
    const { currentWindowWidth } = useWindowWidth();
    const { total } = useSelector((store) => store.clinicalSimulation.unreadCounts);
    const get = useLang('PatientInfo');
    const [hoveredQuickToggleBtn, setHoveredQuickToggleBtn] = useState(null);
    const [hoveredPopoverItem, setHoveredPopoverItem] = useState(null);
    const [isSettingsMenuOpen, setIsSettingsMenuOpen] = useState(false);
    const closeTimerRef = useRef(null);
    const isHoveringSettingsBtnRef = useRef(false);
    const isHoveringSettingsMenuRef = useRef(false);
    const [iframeWidth, setIframeWidth] = useState(null);
    const isFloatingSettingsButton = onlyTrioIDesignVisible || isTrioIDesignRightmost;
    const floatingSettingsButtonSize = ONLY_TRIO_SETTINGS_BUTTON_SIZE;
    const [floatingSettingsPosition, setFloatingSettingsPosition] = useState(() => {
        if (typeof window === 'undefined') {
            return { x: 0, y: FLOATING_SETTINGS_DEFAULT_TOP };
        }
        return {
            x: Math.max(window.innerWidth - FLOATING_SETTINGS_DEFAULT_RIGHT - floatingSettingsButtonSize, 0),
            y: Math.min(FLOATING_SETTINGS_DEFAULT_TOP, Math.max(window.innerHeight - floatingSettingsButtonSize, 0)),
        };
    });
    const [isDraggingSettingsButton, setIsDraggingSettingsButton] = useState(false);
    const settingsButtonRef = useRef(null);
    const floatingSettingsDragRef = useRef(null);
    const floatingSettingsPositionRef = useRef(floatingSettingsPosition);
    const floatingSettingsAnimationFrameRef = useRef(null);
    const pendingFloatingSettingsPositionRef = useRef(null);

    // 获取 settings 浮动按钮默认位置。
    const getDefaultFloatingSettingsPosition = useCallback(() => {
        if (typeof window === 'undefined') {
            return { x: 0, y: FLOATING_SETTINGS_DEFAULT_TOP };
        }
        return {
            x: Math.max(window.innerWidth - FLOATING_SETTINGS_DEFAULT_RIGHT - floatingSettingsButtonSize, 0),
            y: Math.min(FLOATING_SETTINGS_DEFAULT_TOP, Math.max(window.innerHeight - floatingSettingsButtonSize, 0)),
        };
    }, [floatingSettingsButtonSize]);
    // 获取 settings 浮动按钮可拖动边界。
    const getFloatingSettingsBounds = useCallback(() => {
        if (typeof window === 'undefined') {
            return { left: 0, top: 0, right: 0, bottom: 0 };
        }
        return {
            left: 0,
            top: 0,
            right: Math.max(window.innerWidth - floatingSettingsButtonSize, 0),
            bottom: Math.max(window.innerHeight - floatingSettingsButtonSize, 0),
        };
    }, [floatingSettingsButtonSize]);
    // 限制 settings 浮动按钮在可视范围内。
    const clampFloatingSettingsPosition = useCallback(
        (position) => {
            const bounds = getFloatingSettingsBounds();
            return {
                x: Math.min(Math.max(position.x, bounds.left), bounds.right),
                y: Math.min(Math.max(position.y, bounds.top), bounds.bottom),
            };
        },
        [getFloatingSettingsBounds]
    );
    const updateFloatingSettingsButtonDom = useCallback((position) => {
        if (!settingsButtonRef.current) {
            return;
        }
        if (!isFloatingSettingsButton) {
            settingsButtonRef.current.style.left = '';
            settingsButtonRef.current.style.top = '';
            return;
        }
        settingsButtonRef.current.style.left = `${position.x}px`;
        settingsButtonRef.current.style.top = `${position.y}px`;
    }, [isFloatingSettingsButton]);
    const scheduleFloatingSettingsButtonDomUpdate = useCallback(
        (position) => {
            pendingFloatingSettingsPositionRef.current = position;
            if (floatingSettingsAnimationFrameRef.current) {
                return;
            }
            floatingSettingsAnimationFrameRef.current = window.requestAnimationFrame(() => {
                floatingSettingsAnimationFrameRef.current = null;
                updateFloatingSettingsButtonDom(pendingFloatingSettingsPositionRef.current);
            });
        },
        [updateFloatingSettingsButtonDom]
    );

    useEffect(() => {
        floatingSettingsPositionRef.current = floatingSettingsPosition;
        updateFloatingSettingsButtonDom(floatingSettingsPosition);
    }, [floatingSettingsPosition, isFloatingSettingsButton, updateFloatingSettingsButtonDom]);
    useEffect(() => {
        return () => {
            if (typeof window !== 'undefined' && floatingSettingsAnimationFrameRef.current) {
                window.cancelAnimationFrame(floatingSettingsAnimationFrameRef.current);
            }
        };
    }, []);
    useEffect(() => {
        if (!isFloatingSettingsButton || typeof window === 'undefined') {
            return undefined;
        }
        const updateFloatingSettingsPosition = () => {
            setFloatingSettingsPosition((previousPosition) =>
                previousPosition.x === 0 && previousPosition.y === FLOATING_SETTINGS_DEFAULT_TOP
                    ? getDefaultFloatingSettingsPosition()
                    : clampFloatingSettingsPosition(previousPosition)
            );
        };
        updateFloatingSettingsPosition();
        window.addEventListener('resize', updateFloatingSettingsPosition);
        return () => {
            window.removeEventListener('resize', updateFloatingSettingsPosition);
        };
    }, [
        clampFloatingSettingsPosition,
        getDefaultFloatingSettingsPosition,
        getFloatingSettingsBounds,
        isFloatingSettingsButton,
    ]);

    useEffect(() => {
        if (typeof window === 'undefined' || typeof document === 'undefined') {
            return undefined;
        }
        let resizeObserver = null;
        let mutationObserver = null;
        let observedIframe = null;
        let animationFrame = null;
        let isWindowResizeFallbackActive = false;
        const canUseResizeObserver = typeof window.ResizeObserver === 'function';
        const setObservedIframeWidth = (width) => {
            const nextWidth = Number.isFinite(width) ? Math.round(width) : null;
            setIframeWidth((previousWidth) => (previousWidth === nextWidth ? previousWidth : nextWidth));
        };
        const updateIframeWidth = (iframe) => {
            if (animationFrame) {
                window.cancelAnimationFrame(animationFrame);
            }
            if (!iframe) {
                animationFrame = null;
                setObservedIframeWidth(null);
                return;
            }
            animationFrame = window.requestAnimationFrame(() => {
                animationFrame = null;
                setObservedIframeWidth(iframe.getBoundingClientRect().width);
            });
        };
        const handleWindowResize = () => {
            updateIframeWidth(observedIframe);
        };
        const disconnectIframeObserver = () => {
            if (resizeObserver) {
                resizeObserver.disconnect();
                resizeObserver = null;
            }
            if (isWindowResizeFallbackActive) {
                window.removeEventListener('resize', handleWindowResize);
                isWindowResizeFallbackActive = false;
            }
            observedIframe = null;
        };
        const bindIframeObserver = () => {
            const iframe = document.getElementById('ifrmbox');
            if (iframe === observedIframe) {
                updateIframeWidth(iframe);
                return;
            }
            disconnectIframeObserver();
            if (!iframe) {
                updateIframeWidth(null);
                return;
            }
            observedIframe = iframe;
            updateIframeWidth(iframe);
            if (canUseResizeObserver) {
                resizeObserver = new window.ResizeObserver((entries) => {
                    const width = entries[0]?.contentRect?.width ?? iframe.getBoundingClientRect().width;
                    setObservedIframeWidth(width);
                });
                resizeObserver.observe(iframe);
            } else {
                window.addEventListener('resize', handleWindowResize);
                isWindowResizeFallbackActive = true;
            }
        };
        bindIframeObserver();
        if (document.body && typeof window.MutationObserver === 'function') {
            mutationObserver = new window.MutationObserver(bindIframeObserver);
            mutationObserver.observe(document.body, {
                attributes: !canUseResizeObserver,
                childList: true,
                subtree: true,
            });
        }
        return () => {
            if (mutationObserver) {
                mutationObserver.disconnect();
            }
            disconnectIframeObserver();
            if (animationFrame) {
                window.cancelAnimationFrame(animationFrame);
            }
        };
    }, []);

    const isCompactToolbar = iframeWidth === null ? null :(iframeWidth !== null && iframeWidth < IFRAME_COMPACT_TOOLBAR_BREAKPOINT);
    const QUICK_BUTTON_HORIZONTAL_MARGIN_SHRINK = isCompactToolbar ? '4px 10px': `0 ${QUICK_BUTTON_HORIZONTAL_MARGIN}`
    const clearCloseTimer = () => {
        if (closeTimerRef.current) {
            clearTimeout(closeTimerRef.current);
            closeTimerRef.current = null;
        }
    };

    const scheduleCloseIfNeeded = () => {
        clearCloseTimer();
        closeTimerRef.current = setTimeout(() => {
            if (!isHoveringSettingsBtnRef.current && !isHoveringSettingsMenuRef.current) {
                setIsSettingsMenuOpen(false);
            }
        }, 400);
    };
    useEffect(() => {
        if (typeof setIsCompactToolbar === 'function') {
            setIsCompactToolbar(isCompactToolbar === true);
        }
    }, [isCompactToolbar, setIsCompactToolbar]);
    useEffect(() => {
        return () => {
            if (typeof setIsCompactToolbar === 'function') {
                setIsCompactToolbar(false);
            }
        };
    }, [setIsCompactToolbar]);
    const toggleButtons = [
        {
            label: 'PHOTOS',
            tooltipLabel: 'Photos',
            iconLabel: 'PHOTOS',
            icon: <MdPhotoAlbum size={QUICK_BUTTON_ICON_SIZE} />,
            target: 'photos',
            visibleInList: true,
            active: showPhotos,
            isToggle: true,
        },
        {
            label: 'TRIO IDESIGN',
            tooltipLabel: 'Trio iDesign',
            iconLabel: 'IDESIGN',
            icon: <TrioIDesignIcon size={QUICK_BUTTON_ICON_SIZE} />,
            target: 'trio_design',
            visibleInList: true,
            active: showTrioIDesign,
            isToggle: true,
        },
        {
            label: 'I.S.D. VIEWER',
            tooltipLabel: 'I.S.D. Viewer',
            iconLabel: 'I.S.D.\nVIEWER',
            icon: <ISDViewerIcon size={QUICK_BUTTON_ICON_SIZE} />,
            target: 'iso_viewer',
            visibleInList: isPatientSmileDesignHave ?? true,
            active: isShowSmileDesignFeature,
            isToggle: true,
        },
        {
            label: 'COMMENT BOX',
            tooltipLabel: 'Comment Box',
            iconLabel: 'COMMENT',
            icon: <MdComment size={QUICK_BUTTON_ICON_SIZE} />,
            target: 'comment_box',
            visibleInList: true,
            active: comment,
            isToggle: true,
        },
        {
            label: 'LIVE SUPPORT',
            tooltipLabel: 'Live Support',
            iconLabel: 'SUPPORT',
            icon: (
                <Badge
                    className={chatStyle.customBadge}
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                    badgeContent={total}
                >
                    <IoChatbox size={QUICK_BUTTON_ICON_SIZE} />
                </Badge>
            ),
            target: 'chatbox',
            visibleInList: !!isloaded,
            active: showChatbox,
            isToggle: true,
        },
        {
            label: 'SWITCH PLAN',
            tooltipLabel: 'Switch Plan',
            iconLabel: 'SWITCH\nPLAN',
            icon: <IoSwapHorizontalSharp size={QUICK_BUTTON_ICON_SIZE} />,
            target: 'switch_treatment_plan',
            visibleInList: isShowSmileDesignFeature && showTrioIDesign,
            active: switchPositionOfiDesignAndSmileDesign,
            isToggle: true,
        },
    ].filter((f) => f.visibleInList);

    const quickToggleButtons = toggleButtons.filter(
        // (f) => f.visibleInList
        (f) => ['photos', 'trio_design', 'comment_box', 'chatbox', 'iso_viewer'].includes(f.target) && f.visibleInList
    );

    const handleSwitchComponents = (target, checked) => {
        switch (target) {
            case 'photos':
                setShowPhotos(checked);
                break;

            /**
             * Important:
             * - When the `trio_design` toggle is disabled, the layout currently breaks
             *   and the entire screen becomes blank.
             * - Root cause not yet identified; requires review and assistance from the team.
             */
            case 'trio_design':
                setShowTrioIDesign(checked);
                break;

            case 'iso_viewer':
                setIsShowSmileDesignFeature(checked);
                break;

            case 'comment_box':
                // dispatch(toggle(checked));
                setCommentBox(checked);
                break;

            case 'chatbox':
                setShowChatbox(checked);
                break;

            case 'switch_treatment_plan':
                setSwitchPositionOfiDesignAndSmileDesign(checked);
                break;

            default:
                break;
        }
    };

    const handleActionClick = (item) => {
        if (item.isToggle) {
            switch (item.target) {
                case 'photos':
                    setShowPhotos(!item.active);
                    break;
                case 'trio_design':
                    setShowTrioIDesign(!item.active);
                    break;
                case 'iso_viewer':
                    setIsShowSmileDesignFeature(!item.active);
                    break;
                case 'comment_box':
                    setCommentBox(!item.active);
                    break;
                case 'chatbox':
                    setShowChatbox(!item.active);
                    break;
                case 'switch_treatment_plan':
                    setSwitchPositionOfiDesignAndSmileDesign(!item.active);
                    break;
                default:
                    break;
            }
        } else if (item.onClick) {
            item.onClick();
        }
    };

    const getQuickButtonVisualState = (isActive, target) => {
        const isHovered = hoveredQuickToggleBtn === target;
        const isHighlighted = isActive || isHovered;
        const showBackground = isHighlighted && target !== 'settings_menu';
        const isCommentBox = target === 'comment_box';

        return {
            isHovered,
            backgroundColor: showBackground ? I_DESIGN_PRIMARY_PURPLE : 'transparent',
            color: showBackground ? '#fff' : I_DESIGN_MORE_MENU_BACKGROUND,
            textColor: showBackground ? '#fff' : I_DESIGN_PRIMARY_PURPLE,
            letterSpacing: isCommentBox? '-0.4px' : I_DESIGN_LETTER_SPACING,
        };
    };

    const scaledGridTemplateColumns = gridTemplateArray
        .map((column) => {
            const trimmedColumn = String(column).trim();
            if (!trimmedColumn.endsWith('px')) {
                return trimmedColumn;
            }

            const numericValue = Number.parseFloat(trimmedColumn);
            if (Number.isNaN(numericValue)) {
                return trimmedColumn;
            }

            return `${numericValue / I_DESIGN_REFERENCE_SCALE}px`;
        })
        .join(' ');

    const toolbarOriginalHeight = Number.parseFloat(QUICK_BUTTON_HEIGHT) + TOOLBAR_VERTICAL_PADDING * 2;
    const toolbarBottomGap = isCompactToolbar ? 16.5 : -(toolbarOriginalHeight * (1 - I_DESIGN_REFERENCE_SCALE));

    const settingsButtonState = getQuickButtonVisualState(isSettingsMenuOpen, 'settings_menu');
    
    const floatingSettingsButtonStyle = isFloatingSettingsButton
        ? {
              background: isSettingsMenuOpen ? I_DESIGN_PRIMARY_PURPLE : I_DESIGN_MENU_BACKGROUND,
              color: isSettingsMenuOpen ? '#fff' : I_DESIGN_PRIMARY_PURPLE,
              borderRadius: QUICK_BUTTON_BORDER_RADIUS_ROUND,
          }
        : {};
    const settingsButtonSize = isFloatingSettingsButton
        ? `${floatingSettingsButtonSize}px`
        : isCompactToolbar
          ? QUICK_BUTTON_HEIGHT_SHRINK
          : QUICK_BUTTON_HEIGHT;
    const settingsButtonWidth = isFloatingSettingsButton
        ? `${floatingSettingsButtonSize}px`
        : isCompactToolbar
          ? QUICK_BUTTON_WIDTH_SHRINK
          : QUICK_BUTTON_WIDTH;
    const settingsIconSize = isFloatingSettingsButton
        ? QUICK_BUTTON_ICON_SIZE
        : isCompactToolbar
          ? COMPACT_QUICK_BUTTON_ICON_SIZE
          : QUICK_BUTTON_ICON_SIZE;
    // 开始拖动 settings 浮动按钮并隐藏菜单。
    const handleFloatingSettingsPointerDown = (event) => {
        if (!isFloatingSettingsButton || typeof window === 'undefined') {
            return;
        }
        event.preventDefault();
        if (event.currentTarget.setPointerCapture) {
            event.currentTarget.setPointerCapture(event.pointerId);
        }
        clearCloseTimer();
        setIsDraggingSettingsButton(true);
        setIsSettingsMenuOpen(false);
        floatingSettingsDragRef.current = {
            startX: event.clientX,
            startY: event.clientY,
            originX: floatingSettingsPositionRef.current.x,
            originY: floatingSettingsPositionRef.current.y,
        };
    };
    // 拖动时同步 settings 浮动按钮位置。
    const handleFloatingSettingsPointerMove = (event) => {
        const dragState = floatingSettingsDragRef.current;
        if (!dragState) {
            return;
        }
        const nextPosition = clampFloatingSettingsPosition({
            x: dragState.originX + event.clientX - dragState.startX,
            y: dragState.originY + event.clientY - dragState.startY,
        });
        floatingSettingsPositionRef.current = nextPosition;
        scheduleFloatingSettingsButtonDomUpdate(nextPosition);
    };
    // 结束拖动后重新显示菜单。
    const handleFloatingSettingsPointerUp = (event) => {
        if (!floatingSettingsDragRef.current) {
            return;
        }
        floatingSettingsDragRef.current = null;
        setIsDraggingSettingsButton(false);
        isHoveringSettingsBtnRef.current = true;
        if (event.currentTarget.hasPointerCapture && event.currentTarget.hasPointerCapture(event.pointerId)) {
            event.currentTarget.releasePointerCapture(event.pointerId);
        }
        setFloatingSettingsPosition(floatingSettingsPositionRef.current);
        setIsSettingsMenuOpen(true);
    };

    const commentSectionWidth = useMemo(() => {
        if (currentWindowWidth > 1536) {
            return 450;
        }
        return 400
    }, [currentWindowWidth]);

    const isISDViewerOpen = isShowSmileDesignFeature;
    const isIDesignViewerOpen = showTrioIDesign;
    const isCommentBoxOpen = comment;
    const isLiveSupportOpen = showChatbox;
    const hasAnyApprovalContextOpen =
        isISDViewerOpen || isIDesignViewerOpen || isCommentBoxOpen || isLiveSupportOpen;
    const hasAnyViewerPanelOpen = isISDViewerOpen || isIDesignViewerOpen;
    const shouldShowToolButtons = !isFloatingSettingsButton && !isCompactToolbar && quickToggleButtons.length > 0;

    const shouldShowTopApproveButton =
        canApproveCurrentISDPlan &&
        !isFloatingSettingsButton &&
        hasAnyApprovalContextOpen &&
        hasAnyViewerPanelOpen &&
        !isCommentBoxOpen;


    return (
        <div
            style={{
                display: 'flex',
                gridTemplateColumns: scaledGridTemplateColumns,
                background: I_DESIGN_MENU_BACKGROUND,
                transformOrigin: 'left top',
                padding: isFloatingSettingsButton ? 0 : (isCompactToolbar ? '3px 0' : `${TOOLBAR_VERTICAL_PADDING}px 0px`),
                width: isFloatingSettingsButton ? 'auto' : I_DESIGN_REFERENCE_INVERSE_WIDTH,
                transform: isFloatingSettingsButton ? 'none' : `scale(${I_DESIGN_REFERENCE_SCALE})`,
                marginBottom: isFloatingSettingsButton ? 0 : `${toolbarBottomGap}px`,
                justifyContent: 'flex-end',
                alignItems: 'center',
            }}
        >
            <Box
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    paddingLeft: 0,
                    width: isFloatingSettingsButton ? 'auto' : `${commentSectionWidth / I_DESIGN_REFERENCE_SCALE}px`,
                }}
            >
                {shouldShowTopApproveButton && (
                    <Button
                        type="button"
                        variant="contained"
                        onClick={handleTopApproveTreatmentPlan}
                        style={{
                            minWidth: '250px',
                            height: '52px',
                            marginRight: '12px',
                            background: '#39a8bd',
                            color: '#fff',
                            fontSize: '15px',
                            fontWeight: 600,
                            borderRadius: '8px',
                            flexShrink: 0,
                            marginBottom: '2px',
                        }}
                    >
                        {get('@PatientInfo.clinicalSimulation.buttons.approveTreatmentPlan')}
                    </Button>
                )}

                {/* Action Buttons */}
                <Box style={{
                        display: shouldShowToolButtons ? 'flex' : 'none',
                        gap: 0.5,
                        justifyContent: 'center',
                        width: shouldShowToolButtons ? '100%' : 'auto',
                        paddingLeft: '20px',
                    }}>
                    {quickToggleButtons.map((item) => {
                        const quickButtonState = getQuickButtonVisualState(item.active, item.target);

                        return (
                            <IDesignTooltip
                                key={item.target}
                                title={item.tooltipLabel ?? item.label}
                                placement="bottom"
                                arrow
                                enterDelay={400}
                                enterNextDelay={400}
                                interactive
                                leaveDelay={150}
                            >
                                <IconButton
                                    aria-label={item.label}
                                    disableRipple
                                    onClick={() => handleActionClick(item)}
                                    onMouseEnter={() => setHoveredQuickToggleBtn(item.target)}
                                    onMouseLeave={() => setHoveredQuickToggleBtn(null)}
                                    style={{
                                        background: quickButtonState.backgroundColor,
                                        color: quickButtonState.color,
                                        borderRadius: QUICK_BUTTON_BORDER_RADIUS,
                                        height: QUICK_BUTTON_HEIGHT,
                                        width: QUICK_BUTTON_WIDTH,
                                        margin: `0 ${QUICK_BUTTON_HORIZONTAL_MARGIN} 0.5px ${QUICK_BUTTON_HORIZONTAL_MARGIN}`,
                                        padding: 0,
                                        transition: 'none',
                                    }}
                                >
                                    <div
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            height: '100%',
                                            width: '100%',
                                            gap: '4px',
                                            padding: QUICK_BUTTON_PADDING,
                                        }}
                                    >
                                        {item.icon}
                                        <div
                                            style={{
                                                color: quickButtonState.textColor,
                                                fontFamily: TOOL_LABEL_FONT_FAMILY,
                                                fontWeight: QUICK_BUTTON_LABEL_FONT_WEIGHT,
                                                fontSize: QUICK_BUTTON_LABEL_FONT_SIZE,
                                                lineHeight: QUICK_BUTTON_LABEL_LINE_HEIGHT,
                                                textAlign: 'center',
                                                whiteSpace: 'pre-line',
                                                wordBreak: 'keep-all',
                                                overflowWrap: 'normal',
                                                height: QUICK_BUTTON_LABEL_HEIGHT,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                textTransform: 'uppercase',
                                                letterSpacing: quickButtonState.letterSpacing,
                                                padding: '0 2px',
                                                ...I_DESIGN_TEXT_RENDERING,
                                            }}
                                        >
                                            {item.iconLabel}
                                        </div>
                                    </div>
                                </IconButton>
                            </IDesignTooltip>
                        );
                    })}
                </Box>

                <Box
                    style={{
                        marginLeft: isFloatingSettingsButton
                            ? 0
                            : shouldShowToolButtons
                              ? 'auto'
                              : shouldShowTopApproveButton
                                ? '8px'
                                : 'auto',
                        flexShrink: 0,
                        display: 'flex',
                        alignItems: 'center',
                    }}
                >
                    <IDesignMenuTooltip
                        title={
                            <Box style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                {toggleButtons
                                    .filter((f) => f.visibleInList)
                                    .map((item) => {
                                        const quickButtonState = getQuickButtonVisualState(item.active, item.target);
                                        return (
                                        <Box
                                            key={item.target + '-toggle-btns'}
                                            onMouseEnter={() => setHoveredPopoverItem(item.target)}
                                            onMouseLeave={() => setHoveredPopoverItem(null)}
                                            onClick={() => handleSwitchComponents(item.target, !item.active)}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 10,
                                                paddingRight: 15,
                                                borderRadius: 4,
                                                background:
                                                    hoveredPopoverItem === item.target
                                                        ? I_DESIGN_PRIMARY_PURPLE
                                                        : 'transparent',
                                                color: '#fff',
                                                cursor: 'pointer',
                                                transition:
                                                    'background-color 0.2s ease, transform 0.2s ease, opacity 0.2s ease',
                                                transform:
                                                    hoveredPopoverItem === item.target ? 'translateX(-1px)' : 'none',

                                                // background: quickButtonState.backgroundColor,
                                                // color: quickButtonState.color,
                                            }}
                                        >
                                            <Box
                                                style={{
                                                    width: 50,
                                                    height: 42,
                                                    borderRadius: '8px 0 0 8px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    flexShrink: 0,
                                                }}
                                            >
                                                {item.icon}
                                            </Box>
                                            <Box
                                                style={{
                                                    flexGrow: 1,
                                                    fontFamily: TOOL_LABEL_FONT_FAMILY,
                                                    fontSize: '11px',
                                                    // lineHeight: '12px',
                                                    fontWeight: 600,
                                                    textTransform: 'uppercase',
                                                    letterSpacing: I_DESIGN_LETTER_SPACING,
                                                    ...I_DESIGN_TEXT_RENDERING,
                                                }}
                                            >
                                                {item.label}
                                            </Box>
                                            <ToolsMenuSwitch
                                                disableRipple
                                                checked={item.active}
                                                onClick={(event) => event.stopPropagation()}
                                                onChange={(check) =>
                                                    handleSwitchComponents(item.target, check.target.checked)
                                                }
                                                inputProps={{ 'aria-label': `${item.label}-toggle` }}
                                            />
                                        </Box>)

                                    })}
                                {currentTreatmentPlan?.smileDesignPPT && (
                                    <>
                                        <div
                                            style={{
                                                borderTop: '1px solid rgba(255, 255, 255, 0.24)',
                                                margin: '2px 0',
                                            }}
                                        />
                                        <Box
                                            onMouseEnter={() => setHoveredPopoverItem('isd_report')}
                                            onMouseLeave={() => setHoveredPopoverItem(null)}
                                            onClick={() => {
                                                if (currentTreatmentPlan['smileDesignPPT']) {
                                                    window.open(
                                                        ApiServerUrl +
                                                        currentTreatmentPlan['smileDesignPPT'].substring(1),
                                                        '_parent'
                                                    );
                                                }
                                            }}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 10,
                                                paddingRight: 15,
                                                borderRadius: 4,
                                                background:
                                                    hoveredPopoverItem === 'isd_report'
                                                        ? I_DESIGN_PRIMARY_PURPLE
                                                        : 'transparent',
                                                color: '#fff',
                                                cursor: 'pointer',
                                                transition:
                                                    'background-color 0.2s ease, transform 0.2s ease, opacity 0.2s ease',
                                                transform:
                                                    hoveredPopoverItem === 'isd_report' ? 'translateX(-1px)' : 'none',
                                            }}
                                        >
                                            <Box
                                                style={{
                                                    width: 50,
                                                    height: 50,
                                                    borderRadius: '8px 0 0 8px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    flexShrink: 0,
                                                }}
                                            >
                                                <IoNewspaper size={22} />
                                            </Box>
                                            <Box
                                                style={{
                                                    flexGrow: 1,
                                                    fontFamily: TOOL_LABEL_FONT_FAMILY,
                                                    fontSize: '11px',
                                                    lineHeight: '12px',
                                                    fontWeight: 600,
                                                    textTransform: 'uppercase',
                                                    letterSpacing: I_DESIGN_LETTER_SPACING,
                                                    ...I_DESIGN_TEXT_RENDERING,
                                                }}
                                            >
                                                I.S.D. REPORT
                                            </Box>
                                        </Box>
                                    </>
                                )}
                            </Box>
                        }
                        open={isSettingsMenuOpen && !isDraggingSettingsButton}
                        interactive
                        placement="bottom-end"
                        enterDelay={0}
                        leaveDelay={150}
                        PopperProps={{
                            onMouseEnter: () => {
                                isHoveringSettingsMenuRef.current = true;
                                clearCloseTimer();
                            },
                            onMouseLeave: () => {
                                isHoveringSettingsMenuRef.current = false;
                                scheduleCloseIfNeeded();
                            },
                        }}
                    >
                        <IconButton
                            ref={settingsButtonRef}
                            disableRipple
                            onPointerDown={handleFloatingSettingsPointerDown}
                            onPointerMove={handleFloatingSettingsPointerMove}
                            onPointerUp={handleFloatingSettingsPointerUp}
                            onPointerCancel={handleFloatingSettingsPointerUp}
                            onMouseEnter={() => {
                                if (isDraggingSettingsButton) {
                                    return;
                                }
                                isHoveringSettingsBtnRef.current = true;
                                setHoveredQuickToggleBtn('settings_menu');
                                clearCloseTimer();
                                setIsSettingsMenuOpen(true);
                            }}
                            onMouseLeave={() => {
                                isHoveringSettingsBtnRef.current = false;
                                setHoveredQuickToggleBtn(null);
                                scheduleCloseIfNeeded();
                            }}
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                background: I_DESIGN_MENU_BACKGROUND,
                                color: I_DESIGN_PRIMARY_PURPLE,
                                borderRadius:
                                    floatingSettingsButtonStyle.borderRadius ??
                                    (isFloatingSettingsButton ? QUICK_BUTTON_BORDER_RADIUS_ROUND : QUICK_BUTTON_BORDER_RADIUS),
                                height: settingsButtonSize,
                                width: settingsButtonWidth,
                                margin: isFloatingSettingsButton ? 0 : QUICK_BUTTON_HORIZONTAL_MARGIN_SHRINK,
                                border: 0,
                                cursor: isDraggingSettingsButton ? 'move' : 'pointer',
                                padding: 0,
                                transition: 'none',
                                ...(isFloatingSettingsButton
                                    ? {
                                          position: 'fixed',
                                          left: `${floatingSettingsPosition.x}px`,
                                          top: `${floatingSettingsPosition.y}px`,
                                          zIndex: 999,
                                      }
                                    : {}),
                            }}
                            // className={!isFloatingSettingsButton && isCompactToolbar ? 'settingsMenuButton' : ''}
                        >
                            {
                                // isFloatingSettingsButton || !isCompactToolbar
                                //     ? 
                                // <MdSettings size={settingsIconSize} style={{ flexShrink: 0 }} />
                                <svg width="38" height="39" viewBox="0 0 226 231" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <circle cx="74.5" cy="67.5" r="23.5" fill="#532a74"/>
                                    <path d="M75.2727 208C62.697 208 52.0076 203.674 43.2045 195.021C34.4015 186.368 30 175.861 30 163.5C30 151.139 34.4015 140.632 43.2045 131.979C52.0076 123.326 62.697 119 75.2727 119H150.727C163.303 119 173.992 123.326 182.795 131.979C191.598 140.632 196 151.139 196 163.5C196 175.861 191.598 186.368 182.795 195.021C173.992 203.674 163.303 208 150.727 208H75.2727ZM150.727 185.75C157.015 185.75 162.36 183.587 166.761 179.26C171.163 174.934 173.364 169.681 173.364 163.5C173.364 157.319 171.163 152.066 166.761 147.74C162.36 143.413 157.015 141.25 150.727 141.25C144.439 141.25 139.095 143.413 134.693 147.74C130.292 152.066 128.091 157.319 128.091 163.5C128.091 169.681 130.292 174.934 134.693 179.26C139.095 183.587 144.439 185.75 150.727 185.75Z" fill="#532a74"/>
                                    <path d="M75.2725 25H150.728C162.79 25.0001 172.972 29.1279 181.394 37.4053C189.813 45.6814 194 55.6744 194 67.5C194 79.3256 189.813 89.3186 181.394 97.5947C172.972 105.872 162.79 110 150.728 110H75.2725C63.2103 110 53.0276 105.872 44.6064 97.5947C36.1866 89.3186 32 79.3256 32 67.5C32 55.6744 36.1866 45.6814 44.6064 37.4053C53.0276 29.1279 63.2103 25.0001 75.2725 25ZM75.2725 43.25C68.4713 43.2501 62.6202 45.6118 57.8369 50.3135C53.0522 55.0165 50.6367 60.7839 50.6367 67.5C50.6367 74.2161 53.0522 79.9835 57.8369 84.6865C62.6202 89.3882 68.4713 91.7499 75.2725 91.75C82.0738 91.75 87.9256 89.3882 92.709 84.6865C97.4935 79.9836 99.9092 74.216 99.9092 67.5C99.9092 60.784 97.4935 55.0164 92.709 50.3135C87.9256 45.6118 82.0738 43.25 75.2725 43.25Z" stroke="#532a74" stroke-width="5"/>
                                </svg>
                                    // : <svg viewBox="64 64 896 896" focusable="false" data-icon="setting" width="14px" height="14px" fill="currentColor" aria-hidden="true"><path d="M924.8 625.7l-65.5-56c3.1-19 4.7-38.4 4.7-57.8s-1.6-38.8-4.7-57.8l65.5-56a32.03 32.03 0 009.3-35.2l-.9-2.6a443.74 443.74 0 00-79.7-137.9l-1.8-2.1a32.12 32.12 0 00-35.1-9.5l-81.3 28.9c-30-24.6-63.5-44-99.7-57.6l-15.7-85a32.05 32.05 0 00-25.8-25.7l-2.7-.5c-52.1-9.4-106.9-9.4-159 0l-2.7.5a32.05 32.05 0 00-25.8 25.7l-15.8 85.4a351.86 351.86 0 00-99 57.4l-81.9-29.1a32 32 0 00-35.1 9.5l-1.8 2.1a446.02 446.02 0 00-79.7 137.9l-.9 2.6c-4.5 12.5-.8 26.5 9.3 35.2l66.3 56.6c-3.1 18.8-4.6 38-4.6 57.1 0 19.2 1.5 38.4 4.6 57.1L99 625.5a32.03 32.03 0 00-9.3 35.2l.9 2.6c18.1 50.4 44.9 96.9 79.7 137.9l1.8 2.1a32.12 32.12 0 0035.1 9.5l81.9-29.1c29.8 24.5 63.1 43.9 99 57.4l15.8 85.4a32.05 32.05 0 0025.8 25.7l2.7.5a449.4 449.4 0 00159 0l2.7-.5a32.05 32.05 0 0025.8-25.7l15.7-85a350 350 0 0099.7-57.6l81.3 28.9a32 32 0 0035.1-9.5l1.8-2.1c34.8-41.1 61.6-87.5 79.7-137.9l.9-2.6c4.5-12.3.8-26.3-9.3-35zM788.3 465.9c2.5 15.1 3.8 30.6 3.8 46.1s-1.3 31-3.8 46.1l-6.6 40.1 74.7 63.9a370.03 370.03 0 01-42.6 73.6L721 702.8l-31.4 25.8c-23.9 19.6-50.5 35-79.3 45.8l-38.1 14.3-17.9 97a377.5 377.5 0 01-85 0l-17.9-97.2-37.8-14.5c-28.5-10.8-55-26.2-78.7-45.7l-31.4-25.9-93.4 33.2c-17-22.9-31.2-47.6-42.6-73.6l75.5-64.5-6.5-40c-2.4-14.9-3.7-30.3-3.7-45.5 0-15.3 1.2-30.6 3.7-45.5l6.5-40-75.5-64.5c11.3-26.1 25.6-50.7 42.6-73.6l93.4 33.2 31.4-25.9c23.7-19.5 50.2-34.9 78.7-45.7l37.9-14.3 17.9-97.2c28.1-3.2 56.8-3.2 85 0l17.9 97 38.1 14.3c28.7 10.8 55.4 26.2 79.3 45.8l31.4 25.8 92.8-32.9c17 22.9 31.2 47.6 42.6 73.6L781.8 426l6.5 39.9zM512 326c-97.2 0-176 78.8-176 176s78.8 176 176 176 176-78.8 176-176-78.8-176-176-176zm79.2 255.2A111.6 111.6 0 01512 614c-29.9 0-58-11.7-79.2-32.8A111.6 111.6 0 01400 502c0-29.9 11.7-58 32.8-79.2C454 401.6 482.1 390 512 390c29.9 0 58 11.6 79.2 32.8A111.6 111.6 0 01624 502c0 29.9-11.7 58-32.8 79.2z"></path></svg>
                            }
                        </IconButton>
                    </IDesignMenuTooltip>
                </Box>

            </Box>
        </div >
    );
};

export default ToolsMenuButton;
